import { z } from "./zod";

export const SUPPORT_TOPICS = [
  "Technical problem",
  "Shortcut link or content",
  "Purchase or subscription",
  "Privacy request",
  "Accessibility",
  "Other",
] as const;

export const supportRequestSchema = z.object({
  name: z.string().trim().min(1).max(100).refine((value) => !/[\p{Cc}]/u.test(value), "Enter a valid name."),
  email: z.string().trim().max(254).email(),
  topic: z.enum(SUPPORT_TOPICS),
  message: z.string().trim().min(20).max(5000),
  app_version: z.string().trim().max(30).optional(),
});

export type SupportRequest = z.infer<typeof supportRequestSchema>;

export const ADMIN_IDLE_MS = 15 * 60 * 1000;
const SUPPORT_SUBMIT_WINDOW_MS = 60 * 1000;
const SUPPORT_SUBMIT_MAX = 3;
const LOGIN_LOCK_KEY = "shortcuts-library-admin-lock";
const LOGIN_MAX_FAILURES = 5;
const LOGIN_LOCK_MS = 15 * 60 * 1000;

type StoredLock = { failures: number; lockedUntil: number };

function readSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Private mode can block sessionStorage; server limits still apply. */
  }
}

export function consumeClientRateLimit(key: string, max: number, windowMs: number): boolean {
  const now = Date.now();
  const hits = (readSession<number[]>(key) ?? []).filter((time) => now - time < windowMs);
  if (hits.length >= max) return false;
  writeSession(key, [...hits, now]);
  return true;
}

export function canSubmitSupportForm(): boolean {
  return consumeClientRateLimit("shortcuts-library-support-submits", SUPPORT_SUBMIT_MAX, SUPPORT_SUBMIT_WINDOW_MS);
}

export function adminSignInGate(): { allowed: true; delayMs: number } | { allowed: false; retryAt: number } {
  const lock = readSession<StoredLock>(LOGIN_LOCK_KEY);
  const retryAt = lock?.lockedUntil ?? 0;
  if (retryAt > Date.now()) return { allowed: false, retryAt };
  const failures = lock?.failures ?? 0;
  return { allowed: true, delayMs: failures ? Math.min(8000, 1000 * 2 ** (failures - 1)) : 0 };
}

export function recordAdminSignInFailure() {
  const lock = readSession<StoredLock>(LOGIN_LOCK_KEY) ?? { failures: 0, lockedUntil: 0 };
  const failures = lock.failures + 1;
  writeSession(LOGIN_LOCK_KEY, {
    failures,
    lockedUntil: failures >= LOGIN_MAX_FAILURES ? Date.now() + LOGIN_LOCK_MS : 0,
  });
}

export function clearAdminSignInFailures() {
  try {
    sessionStorage.removeItem(LOGIN_LOCK_KEY);
  } catch {
    /* Ignore */
  }
}

export function wait(ms: number) {
  return ms > 0 ? new Promise((resolve) => window.setTimeout(resolve, ms)) : Promise.resolve();
}

export function publicError(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : typeof error === "object" && error && "message" in error ? String((error as { message?: unknown }).message ?? "") : "";
  if (/too many requests|rate limit|PT429/i.test(message)) return "Too many requests. Wait a minute and try again.";
  if (/administrator access/i.test(message)) return "Administrator access required.";
  if (message && !/(42P01|42703|PGRST|jwt|stack|permission denied|column |relation |syntax error|could not find the function)/i.test(message)) return message;
  if (/could not find the function/i.test(message)) return fallback;
  return fallback;
}

export function retryDelayLabel(retryAt: number): string {
  const minutes = Math.max(1, Math.ceil((retryAt - Date.now()) / 60000));
  return `Too many sign-in attempts. Try again in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`;
}
